import React, { useEffect, useState } from 'react'
import { supabase } from '../../supabaseClient'
import Header from '../../components/Header'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts'
import { useNavigate } from 'react-router-dom'

const COLORS = { active: '#1D9E75', inactive: '#B4B2A9', bar: '#A0826D', top: '#7F77DD' }
const AVATAR_COLORS = ['#A0826D', '#7F77DD', '#1D9E75', '#D85A30', '#D4537E', '#2563EB']
const CARD = "bg-white rounded-2xl border border-gray-200 shadow-[0_1px_4px_rgba(0,0,0,0.05)]"

const fmt = (n) =>
  `₱${Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const fmtShort = (n) =>
  `₱${Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`

const fmtTimeAgo = (iso) => {
  const m = Math.floor((Date.now() - new Date(iso)) / 60000)
  if (m < 1) return 'Just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 30) return `${d}d ago`
  return `${Math.floor(d / 30)}mo ago`
}

function Pagination({ page, totalItems, pageSize, onPageChange }) {
  const totalPages = Math.ceil(totalItems / pageSize)
  if (totalPages <= 1) return null

  return (
    <div className="flex items-center justify-between px-4 py-2">
      <span className="text-[11px] text-gray-400">
        {Math.min((page - 1) * pageSize + 1, totalItems)}–{Math.min(page * pageSize, totalItems)} of {totalItems} customers
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page === 1}
          className="px-2.5 py-1 rounded-lg border text-[11px]"
          style={{
            borderColor: '#e5e7eb',
            color: page === 1 ? '#e5e7eb' : '#6b7280',
            cursor: page === 1 ? 'not-allowed' : 'pointer',
          }}>
          ← Prev
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
          .reduce((acc, n, idx, arr) => {
            if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
            acc.push(n)
            return acc
          }, [])
          .map((n, idx) =>
            n === "…" ? (
              <span key={`e-${idx}`} className="text-[11px] text-gray-400 px-0.5">…</span>
            ) : (
              <button key={n} onClick={() => onPageChange(n)}
                className="w-7 h-7 rounded-lg border text-[11px] font-semibold"
                style={{
                  borderColor: n === page ? '#A0826D' : '#e5e7eb',
                  background: n === page ? '#A0826D' : '#fff',
                  color: n === page ? '#fff' : '#6b7280',
                  cursor: 'pointer',
                }}>
                {n}
              </button>
            )
          )
        }
        <button
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          className="px-2.5 py-1 rounded-lg border text-[11px]"
          style={{
            borderColor: '#e5e7eb',
            color: page === totalPages ? '#e5e7eb' : '#6b7280',
            cursor: page === totalPages ? 'not-allowed' : 'pointer',
          }}>
          Next →
        </button>
      </div>
    </div>
  )
}

export default function CustomerInsight({ onGoToProfile }) {
  const [stats, setStats] = useState(null)
  const [dailyNewCustomers, setDailyNewCustomers] = useState([])
  const [statusData, setStatusData] = useState([])
  const [topCustomers, setTopCustomers] = useState([])
  const [recentCustomers, setRecentCustomers] = useState([])
  const [topCustomer, setTopCustomer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const navigate = useNavigate();

  const PAGE_SIZE = 10
  const pagedCustomers = recentCustomers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => {
    fetchData()

    const ch1 = supabase
      .channel('ci-customers')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'customers' }, fetchData)
      .subscribe()
    const ch2 = supabase
      .channel('ci-orders')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, fetchData)
      .subscribe()

    return () => {
      supabase.removeChannel(ch1)
      supabase.removeChannel(ch2)
    }
  }, [])

  const fetchData = async () => {
    try {
      const { data: customers } = await supabase
        .from('customers')
        .select('*')
        .order('created_at', { ascending: false })

      const { data: orders } = await supabase
        .from('orders')
        .select('user_id, final_amount, order_status, created_at')

      const now = new Date()
      const sevenAgo = new Date(Date.now() - 7 * 86400000)
      const fourteenAgo = new Date(Date.now() - 14 * 86400000)
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1)
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59)

      const total = customers.length
      const active = customers.filter(c => c.account_status === 'active').length
      const activePct = total > 0 ? Math.round((active / total) * 100) : 0
      const new7d = customers.filter(c => new Date(c.created_at) >= sevenAgo).length
      const thisMonthNew = customers.filter(c => new Date(c.created_at) >= thisMonthStart).length
      const lastMonthNew = customers.filter(c => {
        const d = new Date(c.created_at)
        return d >= lastMonthStart && d <= lastMonthEnd
      }).length
      const monthGrowth = lastMonthNew > 0
        ? Number(((thisMonthNew - lastMonthNew) / lastMonthNew * 100).toFixed(0))
        : null

      const orderMap = {}
      const orderCountMap = {}
      const customerSpendMap = {}
      orders.forEach(o => {
        if (o.order_status === 'completed') {
          orderMap[o.user_id] = (orderMap[o.user_id] || 0) + Number(o.final_amount || 0)
          orderCountMap[o.user_id] = (orderCountMap[o.user_id] || 0) + 1
          customerSpendMap[o.user_id] = (customerSpendMap[o.user_id] || 0) + Number(o.final_amount || 0)
        }
      })

      const ltvVals = Object.values(orderMap)
      const avgLTV = ltvVals.length ? ltvVals.reduce((a, b) => a + b, 0) / ltvVals.length : 0
      const returningCount = Object.values(orderCountMap).filter(c => c >= 2).length
      const returningPct = total > 0 ? Math.round((returningCount / total) * 100) : 0

      setStats({ total, active, activePct, new7d, thisMonthNew, lastMonthNew, monthGrowth, avgLTV, returningPct })

      const dayMap = {}
      for (let i = 13; i >= 0; i--) {
        const d = new Date(now)
        d.setDate(d.getDate() - i)
        const key = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        dayMap[key] = 0
      }
      customers.forEach(c => {
        const date = new Date(c.created_at)
        if (date >= fourteenAgo) {
          const key = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          if (dayMap[key] !== undefined) dayMap[key]++
        }
      })
      setDailyNewCustomers(Object.entries(dayMap).map(([date, count]) => ({ date, count })))

      // Status donut
      setStatusData([
        { name: 'Active', value: active },
        { name: 'Inactive', value: total - active },
      ])

      // Top 5 customers by spend 
      const top5 = customers
        .map(c => ({
          name: `${c.first_name?.charAt(0) || ''}. ${c.last_name || ''}`,
          spend: parseFloat((customerSpendMap[c.user_id] || 0).toFixed(2)),
        }))
        .sort((a, b) => b.spend - a.spend)
        .slice(0, 5)
        .reverse()
      setTopCustomers(top5)

      // Recent customers
      const recent = customers.map(c => {
        const cos = orders.filter(o => o.user_id === c.user_id && o.order_status === 'completed')
        const totalSpent = cos.reduce((s, o) => s + Number(o.final_amount || 0), 0)
        return { ...c, orderCount: cos.length, totalSpent }
      })
      setRecentCustomers(recent)

      // Top spender this month
      const thisMonthOrders = orders.filter(
        o => o.order_status === 'completed' && new Date(o.created_at) >= thisMonthStart
      )
      const thisMonthSpend = {}
      thisMonthOrders.forEach(o => {
        thisMonthSpend[o.user_id] = (thisMonthSpend[o.user_id] || 0) + Number(o.final_amount || 0)
      })
      const topUserId = Object.entries(thisMonthSpend).sort((a, b) => b[1] - a[1])[0]?.[0]
      if (topUserId) {
        const topC = customers.find(c => c.user_id === topUserId)
        if (topC) setTopCustomer({ name: `${topC.first_name} ${topC.last_name}`, spent: thisMonthSpend[topUserId] })
      } else {
        setTopCustomer(null)
      }

    } catch (err) {
      console.error('CustomerInsight error:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <Header title="Customer Insights" subtitle="Overview of customer activity and engagement" onGoToProfile={() => navigate('/profile')} />
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#A0826D]" />
      </div>
    </div>
  )

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <Header
        title="Customer Insights"
        subtitle="Overview of customer activity and engagement"
        onGoToProfile={onGoToProfile}
      />

      <div className="p-6 flex flex-col gap-4">

        <div className="grid grid-cols-2 gap-3">
          <div className={`${CARD} p-4`}>
            <p className="text-xs text-gray-400 mb-1">Total Customers</p>
            <p className="text-3xl font-bold text-gray-800">{stats.total.toLocaleString()}</p>
            {stats.monthGrowth !== null && (
              <p className="text-xs text-gray-400 mt-1">
                <span className={stats.monthGrowth >= 0 ? 'text-green-600 font-semibold' : 'text-red-500 font-semibold'}>
                  {stats.monthGrowth >= 0 ? '+' : ''}{stats.monthGrowth}%
                </span>{' '}vs last month
              </p>
            )}
          </div>

          <div className={`${CARD} p-4`}>
            <p className="text-xs text-gray-400 mb-1">Active Accounts</p>
            <p className="text-3xl font-bold text-green-600">{stats.active.toLocaleString()}</p>
            <p className="text-xs text-gray-400 mt-1">{stats.activePct}% of all customers</p>
          </div>

          <div className={`${CARD} p-4`}>
            <p className="text-xs text-gray-400 mb-1">Returning Buyers</p>
            <p className="text-3xl font-bold text-gray-800">{stats.returningPct}%</p>
            <p className="text-xs text-gray-400 mt-1">placed 2+ orders</p>
          </div>

          <div className={`${CARD} p-4`}>
            <p className="text-xs text-gray-400 mb-1">Avg Customer LTV</p>
            <p className="text-2xl font-bold text-[#A0826D]">{fmtShort(stats.avgLTV)}</p>
            <p className="text-xs text-gray-400 mt-1">completed orders</p>
          </div>
        </div>

        {/* Top spender banner */}
        {topCustomer && (
          <div className={`${CARD} p-4 flex items-center gap-3`}>
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
              style={{ background: '#A0826D' }}
            >
              {topCustomer.name.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Top Spender This Month</p>
              <p className="text-sm font-semibold text-gray-800 truncate">{topCustomer.name}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-sm font-bold text-[#A0826D]">{fmtShort(topCustomer.spent)}</p>
              <p className="text-[10px] text-gray-400">this month</p>
            </div>
          </div>
        )}

        {/* New customers bar chart — last 14 days */}
        <div className={`${CARD} p-4`}>          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
            New customers — last 14 days
          </p>
          <ResponsiveContainer debounce={200} width="100%" height={160}>
            <BarChart data={dailyNewCustomers} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: '#888780' }}
                interval={1}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#888780' }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '0.5px solid #e5e7eb' }}
                formatter={(v) => [v, 'New customers']}
              />
              <Bar dataKey="count" fill={COLORS.bar} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-2 gap-3">

          {/* Active vs inactive donut */}
          <div className={`${CARD} p-4`}>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Account status
            </p>
            <div className="flex gap-3 mb-2">
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: COLORS.active }} />
                Active
              </span>
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: COLORS.inactive }} />
                Inactive
              </span>
            </div>
            <ResponsiveContainer debounce={200} width="100%" height={150}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius="52%"
                  outerRadius="78%"
                  dataKey="value"
                  strokeWidth={0}
                >
                  {statusData.map((_, i) => (
                    <Cell key={i} fill={i === 0 ? COLORS.active : COLORS.inactive} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '0.5px solid #e5e7eb' }}
                  formatter={(v, name) => [v.toLocaleString(), name]}
                />
                <text x="50%" y="44%" textAnchor="middle" dominantBaseline="middle"
                  fill={COLORS.active} fontSize={18} fontWeight={500}>
                  {stats.activePct}%
                </text>
                <text x="50%" y="58%" textAnchor="middle" dominantBaseline="middle"
                  fill="#888780" fontSize={11}>
                  active
                </text>
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className={`${CARD} p-4`}>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
              Top by spend
            </p>
            <ResponsiveContainer debounce={200} width="100%" height={150}>
              <BarChart
                layout="vertical"
                data={topCustomers}
                margin={{ top: 0, right: 8, left: 0, bottom: 0 }}
              >
                <XAxis
                  type="number"
                  tick={{ fontSize: 10, fill: '#888780' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `₱${(v / 1000).toFixed(0)}k`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 10, fill: '#888780' }}
                  tickLine={false}
                  axisLine={false}
                  width={52}
                />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '0.5px solid #e5e7eb' }}
                  formatter={(v) => [fmt(v), 'Lifetime spend']}
                />
                <Bar dataKey="spend" fill={COLORS.top} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent customers list */}
        <div className={`${CARD} overflow-hidden`}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Recent Customers</p>
            <p className="text-xs text-gray-400">Latest 6 joined</p>
          </div>
          {recentCustomers.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No customers yet</p>
          ) : (
            <div className="divide-y divide-gray-50">
              {pagedCustomers.map((c, i) => (
                <div key={c.user_id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                    style={{ backgroundColor: AVATAR_COLORS[i % AVATAR_COLORS.length] }}
                  >
                    {c.first_name?.charAt(0) || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {c.first_name} {c.last_name}
                    </p>
                    <p className="text-xs text-gray-400">
                      {c.orderCount} order{c.orderCount !== 1 ? 's' : ''} · {fmt(c.totalSpent)} lifetime
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                      c.account_status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {c.account_status}
                    </span>
                    <span className="text-[10px] text-gray-400">{fmtTimeAgo(c.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Pagination
            page={page}
            totalItems={recentCustomers.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>

        <p className="text-xs text-center text-gray-400 pb-1">
          Deep analytics, lifecycle & churn data available in the{' '}
          <span className="text-[#A0826D] font-medium">Customer Intelligence Dashboard</span>
        </p>

      </div>
    </div>
  )
}